import _ from 'lodash';
import log from 'loglevel';
import { defineStore } from 'pinia';
import { watch } from 'vue';
import { getAuth, signInWithCustomToken, signOut } from 'firebase/auth';

import {
    setAuthTokenInLocalStorage,
    getAuthTokenFromLocalStorage,
    removeAuthTokenFromLocalStorage,
    setFirebaseAuthTokenInLocalStorage,
    getFirebaseAuthTokenFromLocalStorage,
    removeFirebaseAuthTokenFromLocalStorage,
} from '@/utils/utils';

import { AuthService } from '@/api/services/AuthService';
import { apiEvents } from '@/api/services/ApiService';
import { logEventLoginMethod } from "@/api/services/AnalyticsTracker";
import { NotificationsStore } from './NotificationsStore';

import { useSnackbar } from '@/composables/useSnackbar';
import type { LoginResponse } from '@/interfaces/Auth/LoginResponse';
import type { GoogleLoginResponse } from '@/interfaces/Auth/GoogleLoginResponse';
import type { FacebookLoginResponse } from '@/interfaces/Auth/FacebookLoginResponse';

/* global google */
declare const google: any;

export const useAuthStore = defineStore({
    id: 'authStore',
    state: () => ({
        authToken: getAuthTokenFromLocalStorage() || '',
        firebaseAuthToken: getFirebaseAuthTokenFromLocalStorage() || '',
        loginDialogVisible: false,
        loginMethod: '' as string,
        googleLoginResponse: {} as GoogleLoginResponse,
        facebookLoginResponse: {} as FacebookLoginResponse,
        failureResponse: null as any,
        isFirebaseAuthenticated: false,
    }),
    actions: {
        async initialize() {
            console.log('Initializing AuthStore');
            try {
                this.setupWatchers();
                this.setupErrorHandler();
                
                // Set up the invalid token handler
                apiEvents.onInvalidToken = () => {
                    log.warn("Invalid auth token detected, logging out user");
                    const { showSnackbar } = useSnackbar();
                    showSnackbar({
                        message: 'Your session has expired. Please log in again.',
                        color: 'warning',
                    });
                    this.logout();
                };
                
                // If we have a Firebase token, try to authenticate immediately
                if (this.firebaseAuthToken) {
                    this.authenticateWithFirebase(this.firebaseAuthToken);
                }
                
                console.log('AuthStore initialized successfully');
                return true;
            } catch (error) {
                log.error("Failed to initialize AuthStore:", error);
                return false;
            }
        },

        setupWatchers() {
            watch(() => this.googleLoginResponse, this.handleGoogleLoginResponseChange, { deep: true });
            watch(() => this.facebookLoginResponse, this.handleFacebookLoginResponseChange, { deep: true });
            watch(() => this.failureResponse, () => {
                const { showSnackbar } = useSnackbar();
                showSnackbar({
                    message: this.failureResponse.response?.data?.error?.friendly_message || 'Error connecting to server',
                    color: 'error',
                });
            }, { deep: true });
            watch(() => this.firebaseAuthToken, (newValue: string, oldValue: string) => {
                if (newValue !== oldValue && newValue) {
                    this.authenticateWithFirebase(newValue);
                }
            });
        },

        /**
         * Authenticates with Firebase using a custom token
         * @param token - The Firebase custom token
         */
        async authenticateWithFirebase(token: string) {
            const auth = getAuth();
            log.info("Attempting Firebase authentication with custom token");
            
            try {
                const userCredential = await signInWithCustomToken(auth, token);
                // Token gets updated in firebase module automatically
                const user = userCredential.user;
                log.info(`Firebase authentication successful. UID: ${user.uid}`);
                this.isFirebaseAuthenticated = true;
                
                // Wait a moment to ensure Firebase auth is fully established
                setTimeout(async () => {
                    try {
                        // Re-initialize the NotificationsStore after successful Firebase auth
                        const notificationsStore = NotificationsStore();
                        if (notificationsStore) {
                            log.info("Initializing NotificationsStore after Firebase auth");
                            await notificationsStore.initialize();
                            log.info("NotificationsStore initialized after Firebase auth");
                        }
                    } catch (err) {
                        log.error("Failed to initialize NotificationsStore after Firebase auth:", err);
                    }
                }, 2000); // Wait 2 seconds to ensure auth is fully established
            } catch (error: any) {
                this.isFirebaseAuthenticated = false;
                const errorCode = error.code;
                const errorMessage = error.message;
                log.error(`Firebase authentication failed: ${errorCode} - ${errorMessage}`);
                
                // If token is invalid, clear it
                if (errorCode === 'auth/invalid-custom-token') {
                    log.warn("Invalid Firebase token, clearing from storage");
                    this.firebaseAuthToken = '';
                    removeFirebaseAuthTokenFromLocalStorage();
                }
            }
        },

        setupErrorHandler() {
            // Set up global API error handler
            apiEvents.onError = (error: any) => {
                this.failureResponse = null; // Clear previous error
                this.failureResponse = error; // Set new error
            };
        },

        async handleGoogleLoginResponseChange(newGoogleLoginResponse: GoogleLoginResponse, oldGoogleLoginResponse: GoogleLoginResponse) {
            if (newGoogleLoginResponse?.credential && !_.isEqual(newGoogleLoginResponse.credential, oldGoogleLoginResponse.credential)) {
                try {
                    const loginResponse: LoginResponse = await AuthService.loginWithGoogle(newGoogleLoginResponse.credential);
                    this.handleSuccessfulLogin(loginResponse);
                } catch (error) {
                    console.error("Google login failed " + error);
                } finally {
                    this.redirectAfterLogin();
                }
            }
        },

        async handleFacebookLoginResponseChange(newFacebookLoginResponse: FacebookLoginResponse, oldFacebookLoginResponse: FacebookLoginResponse) {
            if (newFacebookLoginResponse?.authResponse?.accessToken && !_.isEqual(newFacebookLoginResponse.authResponse.accessToken, oldFacebookLoginResponse.authResponse?.accessToken)) {
                try {
                    const loginResponse: LoginResponse = await AuthService.loginWithFacebook(newFacebookLoginResponse.authResponse.accessToken);
                    this.handleSuccessfulLogin(loginResponse);
                } catch (error) {
                    console.error("Facebook login failed " + error);
                } finally {
                    this.redirectAfterLogin();
                }
            }
        },

        /**
         * Logs in with email and password
         * @param email - The user's email
         * @param password - The user's password
         */
        async loginWithEmail(email: string, password: string) {
            try {
                const response: LoginResponse = await AuthService.emailSignin(email, password);
                this.handleSuccessfulLogin(response);
                this.redirectAfterLogin();
            } catch (error) {
                log.error("Email login failed:", error);
                throw error;
            }
        },

        /**
         * Handles successful login by setting tokens and login method
         * @param loginResponse - The login response from the server
         */
        handleSuccessfulLogin(loginResponse: LoginResponse) {
            setAuthTokenInLocalStorage(loginResponse.auth_token);
            setFirebaseAuthTokenInLocalStorage(loginResponse.firebase_auth_token);
            this.authToken = loginResponse.auth_token;
            this.firebaseAuthToken = loginResponse.firebase_auth_token;
            this.loginMethod = loginResponse.login_method;
            logEventLoginMethod(this.loginMethod);
        },

        /**
         * Checks if the user is currently logged in
         * @returns True if the user is logged in, false otherwise
         */
        isUserLoggedIn() {
            return Boolean(this.authToken && this.authToken.length > 0);
        },

        /**
         * Sets the visibility of the login dialog
         * @param value - Whether the login dialog should be visible
         */
        setLoginDialogVisible(value: boolean) {
            this.loginDialogVisible = value;
        },

        /**
         * Shows the login dialog
         */
        showLoginDialog() {
            this.loginDialogVisible = true;
        },

        /**
         * Hides the login dialog
         */
        hideLoginDialog() {
            this.loginDialogVisible = false;
        },

        /**
         * Redirects the user after login, checking for stored redirect information
         */
        redirectAfterLogin() {
            const redirectOfferId = sessionStorage.getItem('redirectOfferId');
            const redirectPath = sessionStorage.getItem('redirectPath');
            
            if (redirectOfferId && redirectPath) {
                // Clear the stored redirect information
                sessionStorage.removeItem('redirectOfferId');
                sessionStorage.removeItem('redirectPath');
                
                // Redirect to the stored path with the offer ID
                if (redirectPath.startsWith('/offers')) {
                    window.location.href = `/offers/${redirectOfferId}`;
                } else {
                    window.location.href = `${redirectPath}?offerId=${redirectOfferId}`;
                }
            } else {
                // Default redirect to earn page
                window.location.href = "/earn";
            }
        },

        /**
         * Logs the user out by clearing tokens and resetting stores
         */
        async logout() {
            try {
                // 1. First disable Google auto-select if available
                if (typeof google !== 'undefined' && google?.accounts?.id) {
                    google.accounts.id.disableAutoSelect();
                }
                
                // 2. Reset notifications store first to prevent any Firebase listener issues
                const notificationsStore = NotificationsStore();
                if (notificationsStore) {
                    notificationsStore.reset();
                }
                
                // 3. Sign out from Firebase before clearing tokens
                const auth = getAuth();
                if (auth.currentUser) {
                    log.info("Signing out from Firebase");
                    try {
                        await signOut(auth);
                    } catch (firebaseError) {
                        log.warn("Error during Firebase signout:", firebaseError);
                        // Continue with logout even if Firebase fails
                    }
                }
                
                // 4. Clear tokens from state
                this.authToken = '';
                this.firebaseAuthToken = '';
                this.isFirebaseAuthenticated = false;
                
                // 5. Clear tokens from local storage
                removeAuthTokenFromLocalStorage();
                removeFirebaseAuthTokenFromLocalStorage();
                
                log.info("Logout completed successfully");
            } catch (error) {
                log.error("Error during logout:", error);
                // Even if there's an error, ensure tokens are cleared
                this.authToken = '';
                this.firebaseAuthToken = '';
                this.isFirebaseAuthenticated = false;
                removeAuthTokenFromLocalStorage();
                removeFirebaseAuthTokenFromLocalStorage();
                throw error;
            }
        }
    }
}); 